import lz from 'lzutf8';
import { baseResolver } from '@/app/BuilderRenderer';

// System pages + any custom tenant page slug
export type PageSlug = string;

// Build RESOLVER_NAMES from the actual resolver so it's always in sync
const RESOLVER_NAMES = new Set([
  ...Object.keys(baseResolver),
  'div', 'p', 'span', 'img', 'Unknown',
]);

// Nodos navbar que se eliminan de las páginas no-home (ver stripNavbarNodes).
const NAVBAR_RESOLVED_NAMES = new Set(['BuilderNavbar', 'NavbarSimple']);

/**
 * Sanitizes CraftJS node tree — removes unknown components, fixes ROOT, cleans orphans.
 * Same logic as page.tsx's sanitizeCraftData.
 */
function sanitizeCraftData(state: any): any {
  if (!state || typeof state !== 'object') return state;

  const isWrapped = state.nodes && typeof state.nodes === 'object';
  const nodes: Record<string, any> = isWrapped ? { ...state.nodes } : { ...state };

  for (const [id, node] of Object.entries(nodes)) {
    if (!node || typeof node !== 'object') { delete nodes[id]; continue; }
    // Craft serializa los componentes como `{ resolvedName }` y los elementos
    // nativos como un string pelado (`"div"`). El único que el editor persiste
    // así es la capa libre de cada sección —`<Element is="div" custom={{ capaLibre }}>`
    // en `RenderNodoLibre`—, y leer siempre `.resolvedName` daba `undefined` sobre
    // el string: la capa se borraba y con ella todo lo que el dealer soltó adentro.
    //
    // Se acepta SOLO la capa, a propósito: hay árboles viejos con secciones que
    // perdieron su `resolvedName` y quedaron como `"div"` suelto. Ésas se siguen
    // descartando; revivirlas metería en un sitio publicado un nodo que hoy no está.
    const t = (node as any).type;
    const esCapaLibre = typeof t === 'string' && (node as any).custom?.capaLibre === true;
    const rn = esCapaLibre ? t : t?.resolvedName;
    if (!rn || typeof rn !== 'string' || !RESOLVER_NAMES.has(rn)) {
      if (id === 'ROOT') {
        (node as any).type = { resolvedName: 'div' };
      } else {
        delete nodes[id];
        continue;
      }
    }
    if (!Array.isArray((node as any).nodes)) (node as any).nodes = [];
    if (!(node as any).linkedNodes || typeof (node as any).linkedNodes !== 'object') (node as any).linkedNodes = {};
  }

  if (!nodes.ROOT) {
    const childIds = Object.keys(nodes);
    nodes.ROOT = {
      type: { resolvedName: 'div' }, isCanvas: true, props: {},
      displayName: 'App Canvas', custom: {}, parent: null,
      nodes: childIds, linkedNodes: {},
    };
  }
  nodes.ROOT.isCanvas = true;
  nodes.ROOT.parent = null;

  const validIds = new Set(Object.keys(nodes));
  for (const node of Object.values(nodes)) {
    if (Array.isArray((node as any).nodes)) {
      (node as any).nodes = (node as any).nodes.filter((c: string) => validIds.has(c));
    }
    // Los enlazados se limpian igual que los hijos: una referencia a un nodo que
    // ya no está deja al componente pidiéndole a Craft un id que no existe.
    const linked = (node as any).linkedNodes;
    if (linked && typeof linked === 'object') {
      for (const k of Object.keys(linked)) {
        if (!validIds.has(linked[k])) delete linked[k];
      }
    }
  }

  return isWrapped ? { ...state, nodes } : nodes;
}

function decompressState(compressed: string): any | null {
  try {
    let parsed: any = lz.decompress(lz.decodeBase64(compressed));
    while (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { break; }
    }
    return typeof parsed === 'object' && parsed ? sanitizeCraftData(parsed) : null;
  } catch {
    return null;
  }
}

/**
 * Applies AI-generated translations to decompressed builder node data.
 * Returns a NEW object — never mutates the original (important for caching).
 * Translations are stored as { "pageSlug::nodeId::propName": "translated text" }
 */
function applyTranslations(state: any, pageSlug: string, translations: Record<string, string>): any {
  if (!state || !translations || Object.keys(translations).length === 0) return state;

  // Deep clone to avoid mutating cached data
  const cloned = JSON.parse(JSON.stringify(state));

  const isWrapped = cloned.nodes && typeof cloned.nodes === 'object';
  const nodes = isWrapped ? cloned.nodes : cloned;

  for (const [nodeId, node] of Object.entries(nodes) as [string, any]) {
    if (!node?.props || nodeId === 'ROOT') continue;

    // Direct text props
    for (const prop of Object.keys(node.props)) {
      const key = `${pageSlug}::${nodeId}::${prop}`;
      if (key in translations && typeof node.props[prop] === 'string') {
        node.props[prop] = translations[key];
      }
    }

    // Array props (links, items, features, etc.)
    for (const arrProp of ['links']) {
      if (Array.isArray(node.props[arrProp])) {
        node.props[arrProp].forEach((item: any, i: number) => {
          const key = `${pageSlug}::${nodeId}::link_${i}`;
          if (key in translations && item?.text) item.text = translations[key];
        });
      }
    }
    for (const arrProp of ['items', 'features', 'testimonials', 'faqs']) {
      if (Array.isArray(node.props[arrProp])) {
        node.props[arrProp].forEach((item: any, i: number) => {
          for (const field of ['title', 'description', 'text', 'question', 'answer', 'name', 'role']) {
            const key = `${pageSlug}::${nodeId}::${arrProp}_${i}_${field}`;
            if (key in translations && item?.[field]) item[field] = translations[key];
          }
        });
      }
    }

    // About: values and members
    if (Array.isArray(node.props.values)) {
      node.props.values.forEach((val: any, i: number) => {
        const tKey = `${pageSlug}::${nodeId}::values_${i}_title`;
        const dKey = `${pageSlug}::${nodeId}::values_${i}_description`;
        if (tKey in translations && val?.title) val.title = translations[tKey];
        if (dKey in translations && val?.description) val.description = translations[dKey];
      });
    }
    if (Array.isArray(node.props.members)) {
      node.props.members.forEach((m: any, i: number) => {
        const nKey = `${pageSlug}::${nodeId}::members_${i}_name`;
        const rKey = `${pageSlug}::${nodeId}::members_${i}_role`;
        if (nKey in translations && m?.name) m.name = translations[nKey];
        if (rKey in translations && m?.role) m.role = translations[rKey];
      });
    }
    // Footer columns
    if (Array.isArray(node.props.columns)) {
      node.props.columns.forEach((col: any, ci: number) => {
        const titleKey = `${pageSlug}::${nodeId}::col_${ci}_title`;
        if (titleKey in translations && col?.title) col.title = translations[titleKey];
        if (Array.isArray(col?.links)) {
          col.links.forEach((link: any, li: number) => {
            const linkKey = `${pageSlug}::${nodeId}::col_${ci}_link_${li}`;
            if (linkKey in translations && link?.text) link.text = translations[linkKey];
          });
        }
      });
    }
  }

  return cloned;
}

/**
 * Elimina los nodos navbar (BuilderNavbar/NavbarSimple) y su subárbol de un estado
 * Craft.js ya descomprimido/saneado. Se aplica en páginas NO-home: el navbar es
 * fuente única — lo configura el dealer en la home y se renderiza en todo el sitio
 * vía el Navbar del layout (ver ConditionalNavbar). El navbar horneado por página
 * sobra y, peor, queda desincronizado cuando el dealer edita la home.
 *
 * Muta y devuelve el mismo objeto (es un blob fresco recién descomprimido, no
 * compartido con la caché). Limpia también las referencias colgantes en `nodes`
 * y `linkedNodes` de los nodos restantes.
 */
function stripNavbarNodes(state: any): any {
  if (!state || typeof state !== 'object') return state;

  const isWrapped = state.nodes && typeof state.nodes === 'object';
  const nodes: Record<string, any> = isWrapped ? state.nodes : state;

  const navRoots = Object.entries(nodes)
    .filter(([, n]: [string, any]) => NAVBAR_RESOLVED_NAMES.has(n?.type?.resolvedName))
    .map(([id]) => id);
  if (navRoots.length === 0) return state;

  // Recolectar cada navbar + todo su subárbol (hijos en `nodes` y `linkedNodes`).
  const toRemove = new Set<string>();
  const stack = [...navRoots];
  while (stack.length) {
    const id = stack.pop() as string;
    if (toRemove.has(id)) continue;
    toRemove.add(id);
    const node = nodes[id];
    if (!node) continue;
    if (Array.isArray(node.nodes)) stack.push(...node.nodes);
    if (node.linkedNodes && typeof node.linkedNodes === 'object') {
      stack.push(...(Object.values(node.linkedNodes) as string[]));
    }
  }

  for (const id of toRemove) delete nodes[id];

  for (const node of Object.values(nodes)) {
    if (Array.isArray((node as any).nodes)) {
      (node as any).nodes = (node as any).nodes.filter((c: string) => !toRemove.has(c));
    }
    const ln = (node as any).linkedNodes;
    if (ln && typeof ln === 'object') {
      for (const k of Object.keys(ln)) {
        if (toRemove.has(ln[k])) delete ln[k];
      }
    }
  }

  return state;
}

const FOOTER_RESOLVED_NAMES = new Set(['Footer', 'FooterModerno']);

/** Memoiza por árbol: `data` es el mismo objeto mientras viva en la caché de páginas. */
const footerCheckCache = new WeakMap<object, boolean>();

/**
 * ¿El árbol de esta página trae su propio footer?
 *
 * Hace falta porque el footer es un nodo borrable como cualquier otro y `home`
 * es la única página que el editor no regenera nunca (`ensureAllSubPages` la
 * saltea), así que puede quedarse sin footer sin que nada lo reponga: 4 tenants
 * llegaron a producción así. Cuando devuelve false, `BuilderPageWrapper` monta
 * el footer estático para que ninguna página quede sin pie.
 */
export function hasFooterNode(state: any): boolean {
  if (!state || typeof state !== 'object') return false;

  const cached = footerCheckCache.get(state);
  if (cached !== undefined) return cached;

  const nodes: Record<string, any> =
    state.nodes && typeof state.nodes === 'object' ? state.nodes : state;

  const found = Object.values(nodes).some((n: any) =>
    FOOTER_RESOLVED_NAMES.has(n?.type?.resolvedName)
  );

  footerCheckCache.set(state, found);
  return found;
}

/**
 * ¿El árbol de esta página contiene un nodo de este componente?
 *
 * Lo usa `BuilderPageWrapper` con `requireComponent` para las páginas que no
 * pueden quedar vacías: si el dealer borró el bloque que le da sentido a la
 * página (p. ej. el formulario en `/agendar`, que además es el link que reparte
 * por WhatsApp), se monta el fallback en vez de servir una página inútil.
 */
export function hasNodeOfType(state: any, resolvedName: string): boolean {
  if (!state || typeof state !== 'object') return false;
  const nodes: Record<string, any> =
    state.nodes && typeof state.nodes === 'object' ? state.nodes : state;
  return Object.values(nodes).some((n: any) => n?.type?.resolvedName === resolvedName);
}

/**
 * Extracts and decompresses builder data for a specific page from the website config.
 * Supports v3 (multi-page), v2 (home-only dual-theme), and legacy formats.
 */
export function getPageBuilderData(
  config: any,
  pageSlug: PageSlug,
  theme: 'light' | 'dark',
  translations?: Record<string, string> | null,
  /**
   * Conservar el nodo navbar también en páginas no-home. Sólo lo piden los
   * tenants de `usaNavbarInline` (ver `lib/navbarPorTenant`), que apagan el
   * navbar del layout y renderizan el suyo dentro del árbol de cada página.
   * Por defecto `false`: para el resto de los tenants no cambia nada.
   */
  conservarNavbar: boolean = false
): any | null {
  if (!config?.is_enabled || !config?.elements_structure) return null;

  const decompress = (compressed: string) => {
    const state = decompressState(compressed);
    const translated = translations ? applyTranslations(state, pageSlug, translations) : state;
    // El navbar es fuente única (configurado en la home, renderizado por el Navbar
    // del layout). En páginas no-home eliminamos el navbar horneado para que no
    // salga duplicado ni desincronizado. El editor del builder NO pasa por aquí.
    return (pageSlug === 'home' || conservarNavbar) ? translated : stripNavbarNodes(translated);
  };

  // elements_structure may be a string (text column) or already-parsed object (jsonb column)
  const structure = config.elements_structure;
  let envelope: any = null;

  if (typeof structure === 'object' && structure !== null) {
    // Already parsed by Supabase (jsonb column)
    envelope = structure;
  } else {
    const raw = String(structure);
    try {
      envelope = JSON.parse(raw);
    } catch {
      // Not JSON — legacy single-theme format
      if (pageSlug === 'home') return decompress(raw);
      return null;
    }
  }

  if (envelope?.v === 3) {
    const availablePages = Object.keys(envelope.pages || {});
    const pageData = envelope.pages?.[pageSlug];
    if (!pageData) {
      console.warn(`[PageBuilder] Page "${pageSlug}" not found in v3 envelope. Available: ${availablePages.join(', ')}`);
      return null;
    }
    const compressed = theme === 'dark' ? (pageData.dark || pageData.light) : (pageData.light || pageData.dark);
    if (!compressed) return null;
    return decompress(compressed);
  }

  if (envelope?.v === 2) {
    if (pageSlug !== 'home') return null;
    const compressed = theme === 'dark' ? (envelope.dark || envelope.light) : (envelope.light || envelope.dark);
    if (!compressed) return null;
    return decompress(compressed);
  }

  // Legacy unversioned envelope
  if (pageSlug === 'home') {
    const raw = typeof structure === 'string' ? structure : JSON.stringify(structure);
    return decompress(raw);
  }

  return null;
}

/**
 * ¿La automotora armó ESTA página con el builder?
 *
 * Existe para el sitemap, que hoy anuncia /about en todos los sitios. Quien no
 * la armó ve la página estándar, que dice "Esta pagina estara disponible
 * proximamente": mandar a Google una página en construcción es peor que no
 * mandarla. Son 43 automotoras activas.
 *
 * Solo mira el sobre `v:3`, que es donde viven las páginas internas (el home se
 * arma de más formas, pero el home no pasa por acá).
 */
export function tienePaginaDelBuilder(config: any, slug: string): boolean {
  if (!config?.is_enabled || !config?.elements_structure) return false;
  const structure = config.elements_structure;
  let envelope: any = null;
  if (typeof structure === 'object' && structure !== null) {
    envelope = structure;
  } else {
    try {
      envelope = JSON.parse(String(structure));
    } catch {
      return false;
    }
  }
  return envelope?.v === 3 && !!envelope?.pages?.[slug];
}

/**
 * Lista de páginas de SISTEMA que el cliente eligió eliminar/ocultar desde el builder.
 * Se persiste en el envelope v3 (`hiddenSystemPages`). 'home' nunca se incluye.
 *
 * La implementación se mudó a `@/lib/hidden-pages` para que la barra de
 * navegación pueda usarla sin importar este módulo, que arrastra
 * `baseResolver`. Se re-exporta para no tocar a quienes ya la importaban acá.
 */
export { getHiddenSystemPages } from './hidden-pages';
